import type { Home, ResidentItem } from '@lmc/ts-common'
import { getNominative } from 'common/utils'
import { NAMESPACE } from 'locale'
import { emarV2Constants } from 'locale/emarV2'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

type AddMedicationResident = Pick<
  ResidentItem,
  'status' | 'isActiveInitialAssessment'
>

type UseAddMedicationButtonStateReturn = {
  disabled: boolean
  tooltip?: string
}

/**
 * Determines whether the "Add Medication" button should be disabled, and
 * which tooltip to show, across the three agreed blocking conditions.
 * Where more than one condition applies, priority order is:
 * 1. eMAR onboarding not completed
 * 2. Initial Assessment not finalised
 * 3. No medication permission
 */
export const useAddMedicationButtonState = (
  resident?: AddMedicationResident
): UseAddMedicationButtonStateReturn => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  const home = useSelector(
    (state: { global: { home?: Home } }) => state.global.home
  )
  const user = useSelector(
    (state: { global: { user?: { medicationPermission?: boolean } } }) =>
      state.global.user
  )

  const isOnboardingIncomplete = home?.features?.emarOnboarded?.enabled !== true
  const isInitialAssessment =
    resident?.status === 'pre-assessment' ||
    Boolean(resident?.isActiveInitialAssessment)
  const hasMedicationPermission = Boolean(user?.medicationPermission)

  if (isOnboardingIncomplete) {
    return {
      disabled: true,
      tooltip: t(emarV2Constants.ADD_MEDICATION_ONBOARDING_TOOLTIP)
    }
  }

  if (isInitialAssessment) {
    return {
      disabled: true,
      tooltip: t(emarV2Constants.ADD_MEDICATION_ASSESSMENT_TOOLTIP, {
        preferredPeopleNominative: getNominative(
          home?.preferredPeopleNominative,
          t(emarV2Constants.RESIDENT)
        )
      })
    }
  }

  if (!hasMedicationPermission) {
    return {
      disabled: true,
      tooltip: t(emarV2Constants.ADD_MEDICATION_PERMISSION_TOOLTIP)
    }
  }

  return { disabled: false }
}

export default useAddMedicationButtonState
